<template>
    <div class="questionnaire-question-choice">
        <div :class="classChoices">
            <component
                :is="getComponent(option.content.__typename)"
                v-for="(option, index) in question.options"
                :key="index"
                class="card"
                :card="option.content"
                :questionnaire-store
                :is-selected="isSelected(option.id)"
                @toggle="toggle($event as any as boolean, option.id)"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type {
    QuestionnaireAnswerInput,
    QuestionnaireQuestionChoiceFragment,
    QuestionnaireQuestionChoiceOptionFragment,
} from '@/graphql/api'
import { useAnswerChoice } from './composables/useAnswerChoice'
import { useQuestionnaireStore } from '@/components/cms/component/questionnaire/useQuestionnaireStore'

import type { GraphQLTypeName } from '@/helpers'
import { assertNever } from '@/helpers'

import DebugComponent from '@/components/cms/component/DebugComponent.vue'
import CardIcon from './option/CardIcon.vue'
import CardImageCard from './option/CardImageCard.vue'
import CardImage from './option/CardImage.vue'
import CardText from './option/CardText.vue'

const props = defineProps<{
    question: QuestionnaireQuestionChoiceFragment
    answer: QuestionnaireAnswerInput | undefined
}>()

const emit = defineEmits<{
    'update:answer': [answer: QuestionnaireAnswerInput | undefined]
}>()

const getComponent = (
    type: GraphQLTypeName<QuestionnaireQuestionChoiceOptionFragment['content']>
): any => {
    switch (type) {
        case 'QuestionnaireQuestionChoiceOptionContentIconCard':
            return CardIcon
        case 'QuestionnaireQuestionChoiceOptionContentImage':
            return CardImage
        case 'QuestionnaireQuestionChoiceOptionContentImageCard':
            return CardImageCard
        case 'QuestionnaireQuestionChoiceOptionContentText':
            return CardText
        default:
            return assertNever(type, DebugComponent)
    }
}

const isSelected = (optionId: string) =>
    props.question.multiple
        ? props.answer?.answer?.multiChoice?.some(
              (c) => c.referenceId === optionId
          )
        : props.answer?.answer?.singleChoice?.referenceId === optionId

const { addAnswer, removeAnswer } = useAnswerChoice(props, emit)

const toggle = (isSelected: boolean, optionId: string) =>
    (isSelected ? addAnswer : removeAnswer)(optionId)

const questionnaireStore = useQuestionnaireStore()

const classChoices = computed(() =>
    props.question.options[0]?.content.__typename ===
    'QuestionnaireQuestionChoiceOptionContentText'
        ? 'choices-text'
        : 'choices'
)
</script>
<style lang="scss" scoped>
.questionnaire-question-choice {
    .choices {
        width: 100%;
        display: flex;
        flex-wrap: wrap;
        justify-content: center;
    }

    .choices-text {
        margin: 48px auto 0;
        max-width: 506px;
    }
}
</style>
